import React, { useEffect, useRef, useState } from 'react';
import * as http from '../../../../utils/http';
import * as translator from '../../../../utils/translator';
import './MyAccountPendingActions.css';
import PhotoUploadModal from './PhotoUploadModal';
import { clientConfig } from '../../../../config';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { trackOncePerSession } from '../../../../PixelTrackers/trackingUtils';

const checkIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-check-circle.svg`;
const eyeIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-eye.svg`;

interface PendingActionsData {
  needsPhotoUpload: boolean;
  needsIdVerification: boolean;
  requiresIdVerification: boolean;
  isWaitingActions: boolean;
  idVerificationInProgress: boolean;
  frontPhotoDataUrl: string | null;
  sidePhotoDataUrl: string | null;
}

interface Props {
  hashID: string;
  countryID: string;
  language: string;
  initialPendingData?: PendingActionsData;
}

const MyAccountPendingActions: React.FC<Props> = ({ hashID, countryID, language, initialPendingData }) => {
  const t = (key: string) => translator.translate(language, 'my_account', `PendingActions/${key}`);
  const [needsPhotoUpload, setNeedsPhotoUpload] = useState(initialPendingData?.needsPhotoUpload ?? false);
  const [needsIdVerification, setNeedsIdVerification] = useState(initialPendingData?.needsIdVerification ?? false);
  const [loading, setLoading] = useState(!initialPendingData);
  const [jumioSrc, setJumioSrc] = useState<string | null>(null);
  const [jumioLoading, setJumioLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [photoUploadDone, setPhotoUploadDone] = useState(
    !!(initialPendingData && !initialPendingData.needsPhotoUpload && (initialPendingData.frontPhotoDataUrl || initialPendingData.sidePhotoDataUrl))
  );
  const [frontPreviewUrl, setFrontPreviewUrl] = useState<string | null>(initialPendingData?.frontPhotoDataUrl ?? null);
  const [sidePreviewUrl, setSidePreviewUrl] = useState<string | null>(initialPendingData?.sidePhotoDataUrl ?? null);
  const [jumioSubmitted, setJumioSubmitted] = useState(false);
  const [requiresIdVerification, setRequiresIdVerification] = useState(initialPendingData?.requiresIdVerification ?? false);
  const [isWaitingActions, setIsWaitingActions] = useState(initialPendingData?.isWaitingActions ?? false);
  const [idVerificationInProgress, setIdVerificationInProgress] = useState(initialPendingData?.idVerificationInProgress ?? false);
  const completionTrackedRef = useRef(false);
  const initialNeedsPhotoRef = useRef<boolean | null>(
    initialPendingData != null ? !!initialPendingData.needsPhotoUpload : null
  );

  const trackVerificationViewed = (needsPhoto: boolean, needsId: boolean) => {
    if (!needsPhoto && !needsId) return;
    trackOncePerSession(
      'custom',
      'Verification task viewed',
      {
        order_hash_id: hashID,
        needs_photo: needsPhoto,
        needs_id: needsId,
      },
      3,
      `Verification task viewed:${hashID}`
    );
  };

  const trackVerificationCompleted = (_needsPhoto: boolean, needsId: boolean) => {
    if (completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    MainTracker.track('custom', 'Verification task completed', {
      order_hash_id: hashID,
      needs_photo: initialNeedsPhotoRef.current ?? !!_needsPhoto,
      needs_id: needsId,
    });
  };

  const applyPendingData = (data: any) => {
    if (initialNeedsPhotoRef.current === null) {
      initialNeedsPhotoRef.current = !!data.needsPhotoUpload;
    }
    setNeedsPhotoUpload(!!data.needsPhotoUpload);
    setNeedsIdVerification(!!data.needsIdVerification);
    setRequiresIdVerification(!!data.requiresIdVerification);
    setIsWaitingActions(!!data.isWaitingActions);
    setIdVerificationInProgress(!!data.idVerificationInProgress);
    if (data.frontPhotoDataUrl) setFrontPreviewUrl(data.frontPhotoDataUrl);
    if (data.sidePhotoDataUrl) setSidePreviewUrl(data.sidePhotoDataUrl);
    // Photos on the order are always editable; treat presence as "done" for the preview/edit UI.
    if (data.frontPhotoDataUrl || data.sidePhotoDataUrl) {
      setPhotoUploadDone(true);
    } else if (!data.needsPhotoUpload) {
      setPhotoUploadDone(false);
    }
  };

  const fetchPendingActions = () => {
    return http
      .getFromServer(`${countryID}/account/orders/${hashID}/pending-actions`, [http.MIDDLEWARES.CUSTOMER_ID])
      .then((res: any) => applyPendingData(res.data));
  };

  useEffect(() => {
    if (initialPendingData) {
      if (initialPendingData.isWaitingActions || initialPendingData.needsPhotoUpload || initialPendingData.needsIdVerification) {
        trackVerificationViewed(!!initialPendingData.needsPhotoUpload, !!initialPendingData.needsIdVerification);
      }
      return;
    }
    fetchPendingActions()
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [hashID, countryID]);

  useEffect(() => {
    if (loading) return;
    if (isWaitingActions || needsPhotoUpload || needsIdVerification || idVerificationInProgress) {
      trackVerificationViewed(needsPhotoUpload, needsIdVerification || idVerificationInProgress);
    }
  }, [loading, isWaitingActions, needsPhotoUpload, needsIdVerification, idVerificationInProgress, hashID]);

  useEffect(() => {
    if (!jumioSrc) return;
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = event.data && typeof event.data === 'string' ? JSON.parse(event.data) : null;
        if (data?.payload?.value === 'success') {
          setJumioSubmitted(true);
          setJumioSrc(null);
          trackVerificationCompleted(photoUploadDone || !needsPhotoUpload, true);
        }
      } catch {}
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [jumioSrc, photoUploadDone, needsPhotoUpload]);

  const handleBothUploaded = (frontUrl: string, sideUrl: string) => {
    setFrontPreviewUrl(frontUrl);
    setSidePreviewUrl(sideUrl);
    setPhotoUploadDone(true);
    if (!needsIdVerification && !requiresIdVerification && !idVerificationInProgress) {
      trackVerificationCompleted(true, false);
    }
    fetchPendingActions().catch(() => {});
  };

  useEffect(() => {
    if (loading) return;
    if (!needsPhotoUpload && !needsIdVerification && !idVerificationInProgress && (photoUploadDone || jumioSubmitted)) {
      trackVerificationCompleted(photoUploadDone, jumioSubmitted || requiresIdVerification);
    }
  }, [loading, needsPhotoUpload, needsIdVerification, idVerificationInProgress, photoUploadDone, jumioSubmitted, requiresIdVerification]);

  const handleStartJumio = async () => {
    setJumioLoading(true);
    setErrorMessage(null);
    try {
      const res: any = await http.getFromServer(
        `${countryID}/account/orders/${hashID}/jumio-verification`,
        [http.MIDDLEWARES.CUSTOMER_ID]
      );
      if (res.data.status === 'already_verified') {
        setNeedsIdVerification(false);
        trackVerificationCompleted(photoUploadDone || !needsPhotoUpload, true);
      } else {
        setJumioSrc(res.data.jumioWorkflowSrc);
      }
    } catch {
      setErrorMessage('Failed to start ID verification. Please try again.');
    } finally {
      setJumioLoading(false);
    }
  };

  if (loading) return null;

  /* ── State 1: Photo still needed → show step cards + modal ── */
  if (needsPhotoUpload) {
    const showBothCards = needsIdVerification || idVerificationInProgress;

    return (
      <>
        <div className="pending-actions-card">
          <div className="pending-actions-header">
            <p className="pending-actions-title">
              {showBothCards ? t('title_both') : t('title_one')}
            </p>
            <p className="pending-actions-subtitle">
              {t('subtitle')}
            </p>
          </div>

          <div className={`pending-actions-steps${!showBothCards ? ' pending-actions-steps-single' : ''}`}>
            {/* Step 1: Body Photo */}
            <div className="step-card step-card-active">
              <div className="step-card-header">
                <div className="step-number step-number-active">
                  <span className="step-number-text">01</span>
                </div>
                <span className="step-badge step-badge-active">{t('step_badge_active')}</span>
              </div>
              <div className="step-card-body">
                <p className="step-card-title step-card-title-active">{t('photo_title')}</p>
                <p className="step-card-desc step-card-desc-active">
                  {t('photo_desc')}
                </p>
              </div>
              <div className="step-card-action">
                <button
                  className="pending-action-btn pending-action-btn-primary"
                  onClick={() => setShowModal(true)}
                >
                  {t('photo_btn')}
                </button>
              </div>
            </div>

            {/* Step 2: ID Verification — locked if not started, pending if in progress */}
            {showBothCards && (
              idVerificationInProgress ? (
                <div className="step-card step-card-uploaded pa-id-card">
                  <div className="step-card-header">
                    <div className="pa-done-circle pa-done-circle-check">
                      <img src={checkIcon} alt="" className="pa-done-check" />
                    </div>
                    <div className="step-number step-number-active pa-done-circle-number">
                      <span className="step-number-text">02</span>
                    </div>
                    <span className="step-badge pa-badge-uploaded">{t('step_badge_uploaded')}</span>
                  </div>
                  <div className="step-card-body">
                    <p className="step-card-title step-card-title-active">{t('id_uploaded_title')}</p>
                    <p className="step-card-desc step-card-desc-active">{t('id_pending_desc')}</p>
                  </div>
                  {!jumioSrc && (
                    <div className="step-card-action">
                      <button
                        className="pending-action-btn pending-action-btn-primary"
                        onClick={!jumioLoading ? handleStartJumio : undefined}
                        disabled={jumioLoading}
                      >
                        {jumioLoading ? t('id_btn_loading') : t('id_retry_btn')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="step-card step-card-locked">
                  <div className="step-card-header">
                    <div className="step-number step-number-locked">
                      <span className="step-number-text">02</span>
                    </div>
                    <span className="step-badge step-badge-locked">{t('step_badge_locked')}</span>
                  </div>
                  <div className="step-card-body">
                    <p className="step-card-title step-card-title-locked">{t('id_title')}</p>
                    <p className="step-card-desc step-card-desc-locked">
                      {t('id_desc')}
                    </p>
                  </div>
                  <div className="step-card-action">
                    <button className="pending-action-btn pending-action-btn-disabled" disabled>
                      {t('id_btn')}
                    </button>
                  </div>
                </div>
              )
            )}
          </div>

          {jumioSrc && idVerificationInProgress && (
            <div className="pending-actions-jumio-wrapper">
              <iframe src={jumioSrc} allow="camera; microphone" title="ID Verification" />
            </div>
          )}

          {errorMessage && <p className="pending-actions-error">{errorMessage}</p>}

          <div className="pending-actions-divider" />
          <p className="pending-actions-footer">
            {t('footer')}
          </p>
        </div>

        {showModal && (
          <PhotoUploadModal
            hashID={hashID}
            countryID={countryID}
            language={language}
            isWaitingActions={isWaitingActions}
            initialFrontUrl={frontPreviewUrl}
            initialSideUrl={sidePreviewUrl}
            onClose={() => setShowModal(false)}
            onBothUploaded={handleBothUploaded}
          />
        )}
      </>
    );
  }

  /* ── State 2a: Photo done THIS session + ID still pending → two-column layout ── */
  if (needsIdVerification && photoUploadDone && !jumioSubmitted && !idVerificationInProgress) {
    return (
      <>
        <div className="pending-actions-card">
          <div className="pending-actions-header">
            <p className="pending-actions-title">{t('title_both')}</p>
            <p className="pending-actions-subtitle">
              {t('subtitle')}
            </p>
          </div>

          <div className="pending-actions-steps">
            {/* Photo done card */}
            <div className="step-card step-card-uploaded pa-photo-card">
              <div className="step-card-header">
                <div className="pa-done-circle pa-done-circle-check">
                  <img src={checkIcon} alt="" className="pa-done-check" />
                </div>
                <div className="step-number step-number-active pa-done-circle-number">
                  <span className="step-number-text">01</span>
                </div>
                <span className="step-badge pa-badge-uploaded">{t('step_badge_uploaded')}</span>
              </div>
              <div className="step-card-body">
                <p className="step-card-title step-card-title-active">{t('photo_uploaded_title')}</p>
              </div>
              <div className="pa-thumbnails">
                <PhotoThumb previewUrl={frontPreviewUrl} label={t('thumb_label_front')} />
                <PhotoThumb previewUrl={sidePreviewUrl} label={t('thumb_label_side')} />
              </div>
              <button className="pa-edit-link" onClick={() => setShowModal(true)}>
                {t('edit_btn')}
              </button>
            </div>

            {/* ID active card */}
            <div className="step-card step-card-active pa-id-card">
              <div className="step-card-header">
                <div className="step-number step-number-active" style={{ background: '#269c6e' }}>
                  <span className="step-number-text">02</span>
                </div>
                <span className="step-badge step-badge-active">{t('step_badge_active')}</span>
              </div>
              <div className="step-card-body">
                <p className="step-card-title step-card-title-active">{t('id_title')}</p>
                <p className="step-card-desc step-card-desc-active">
                  {t('id_desc')}
                </p>
              </div>
              {!jumioSrc && (
                <div className="step-card-action" style={{ marginTop: 'auto' }}>
                  <button
                    className="pending-action-btn pending-action-btn-primary"
                    onClick={!jumioLoading ? handleStartJumio : undefined}
                    disabled={jumioLoading}
                  >
                    {jumioLoading ? t('id_btn_loading') : t('id_btn')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {jumioSrc && (
            <div className="pending-actions-jumio-wrapper">
              <iframe src={jumioSrc} allow="camera; microphone" title="ID Verification" />
            </div>
          )}

          {errorMessage && <p className="pending-actions-error">{errorMessage}</p>}

          <div className="pending-actions-divider" />
          <p className="pending-actions-footer">
            {t('footer')}
          </p>
        </div>

        {showModal && (
          <PhotoUploadModal
            hashID={hashID}
            countryID={countryID}
            language={language}
            isWaitingActions={isWaitingActions}
            initialFrontUrl={frontPreviewUrl}
            initialSideUrl={sidePreviewUrl}
            onClose={() => setShowModal(false)}
            onBothUploaded={handleBothUploaded}
          />
        )}
      </>
    );
  }

  /* ── State 2c: Photo only uploaded (no ID verification required) ── */
  if (photoUploadDone && !requiresIdVerification) {
    return (
      <div className="pending-actions-card">
        <div className="step-card step-card-uploaded pa-photo-only-card">
          <div className="pa-photo-only-header">
            <div className="pa-done-circle pa-done-circle-check">
              <img src={checkIcon} alt="" className="pa-done-check" />
            </div>
            <div className="step-number step-number-active pa-done-circle-number">
              <span className="step-number-text">01</span>
            </div>
            <div className="pa-photo-only-text">
              <p className="step-card-title step-card-title-active">{t('title_thank_you')}</p>
              <p className="pa-photo-only-subtitle">{t('subtitle_thank_you')}</p>
            </div>
            <span className="step-badge pa-badge-uploaded">{t('step_badge_uploaded')}</span>
          </div>

          <div className="step-card-body pa-photo-only-mobile-body">
            <p className="step-card-title step-card-title-active">{t('photo_uploaded_title')}</p>
            <p className="step-card-desc step-card-desc-active">{t('subtitle_thank_you')}</p>
          </div>

          <div className="pa-wide-thumbs">
            <WidePhotoThumb previewUrl={frontPreviewUrl} label={t('thumb_label_front')} />
            <WidePhotoThumb previewUrl={sidePreviewUrl} label={t('thumb_label_side')} />
          </div>

          <button className="pa-edit-link pa-photo-only-edit" onClick={() => setShowModal(true)}>
            {t('edit_btn')}
          </button>
        </div>

        <div className="pending-actions-divider" />
        <p className="pending-actions-footer">
          {t('footer')}
</p>

        {showModal && (
          <PhotoUploadModal
            hashID={hashID}
            countryID={countryID}
            language={language}
            isWaitingActions={isWaitingActions}
            initialFrontUrl={frontPreviewUrl}
            initialSideUrl={sidePreviewUrl}
            onClose={() => setShowModal(false)}
            onBothUploaded={handleBothUploaded}
          />
        )}
      </div>
    );
  }

  /* ── State 3: Both submitted — waiting for webhook or fully verified ── */
  if (photoUploadDone && requiresIdVerification && (jumioSubmitted || idVerificationInProgress || !needsIdVerification)) {
    return (
      <div className="pending-actions-card">
        <div className="pending-actions-header">
          <p className="pending-actions-title">{t('title_thank_you')}</p>
          <p className="pending-actions-subtitle">{t('subtitle_thank_you')}</p>
        </div>

        <div className="pending-actions-steps">
          {/* Photo: done */}
          <div className="step-card step-card-uploaded pa-photo-card">
            <div className="step-card-header">
              <div className="pa-done-circle pa-done-circle-check">
                <img src={checkIcon} alt="" className="pa-done-check" />
              </div>
              <div className="step-number step-number-active pa-done-circle-number">
                <span className="step-number-text">01</span>
              </div>
              <span className="step-badge pa-badge-uploaded">{t('step_badge_uploaded')}</span>
            </div>
            <div className="step-card-body">
              <p className="step-card-title step-card-title-active">{t('photo_uploaded_title')}</p>
            </div>
            <div className="pa-thumbnails">
              <PhotoThumb previewUrl={frontPreviewUrl} label={t('thumb_label_front')} />
              <PhotoThumb previewUrl={sidePreviewUrl} label={t('thumb_label_side')} />
            </div>
            <button className="pa-edit-link" onClick={() => setShowModal(true)}>
              {t('edit_btn')}
            </button>
          </div>

          {/* ID: done or pending-retry */}
          <div className="step-card step-card-uploaded pa-id-card">
            <div className="step-card-header">
              <div className="pa-done-circle">
                <img src={checkIcon} alt="" className="pa-done-check" />
              </div>
              <span className="step-badge pa-badge-uploaded">{t('step_badge_uploaded')}</span>
            </div>
            <div className="step-card-body">
              <p className="step-card-title step-card-title-active">{t('id_uploaded_title')}</p>
              <p className="step-card-desc step-card-desc-active">
                {idVerificationInProgress && !jumioSubmitted ? t('id_pending_desc') : t('id_uploaded_desc')}
              </p>
            </div>
            <div className="step-card-action" style={{ marginTop: 'auto' }}>
              {idVerificationInProgress && !jumioSubmitted ? (
                !jumioSrc && (
                  <button
                    className="pending-action-btn pending-action-btn-primary"
                    onClick={!jumioLoading ? handleStartJumio : undefined}
                    disabled={jumioLoading}
                  >
                    {jumioLoading ? t('id_btn_loading') : t('id_retry_btn')}
                  </button>
                )
              ) : (
                <a className="pa-help-link" href="https://www.junglescout.com/contact-us/" target="_blank" rel="noopener noreferrer">
                  {t('id_help_link')}
                </a>
              )}
            </div>
          </div>
        </div>

        {jumioSrc && idVerificationInProgress && !jumioSubmitted && (
          <div className="pending-actions-jumio-wrapper">
            <iframe src={jumioSrc} allow="camera; microphone" title="ID Verification" />
          </div>
        )}

        {errorMessage && <p className="pending-actions-error">{errorMessage}</p>}

        <div className="pending-actions-divider" />
        <p className="pending-actions-footer">
          {t('footer')}
</p>

        {showModal && (
          <PhotoUploadModal
            hashID={hashID}
            countryID={countryID}
            language={language}
            isWaitingActions={isWaitingActions}
            initialFrontUrl={frontPreviewUrl}
            initialSideUrl={sidePreviewUrl}
            onClose={() => setShowModal(false)}
            onBothUploaded={handleBothUploaded}
          />
        )}
      </div>
    );
  }

  /* ── State 2b: ID still pending (photo done in a previous session) ── */
  if (needsIdVerification && !idVerificationInProgress) {
    const showPhotos = !!(frontPreviewUrl || sidePreviewUrl || photoUploadDone);
    return (
      <>
        <div className="pending-actions-card">
          <div className="pending-actions-header">
            <p className="pending-actions-title">{showPhotos ? t('title_both') : t('title_one')}</p>
            <p className="pending-actions-subtitle">
              {t('subtitle')}
            </p>
          </div>

          <div className={`pending-actions-steps${!showPhotos ? '' : ''}`}>
            {showPhotos && (
              <div className="step-card step-card-uploaded pa-photo-card">
                <div className="step-card-header">
                  <div className="pa-done-circle pa-done-circle-check">
                    <img src={checkIcon} alt="" className="pa-done-check" />
                  </div>
                  <div className="step-number step-number-active pa-done-circle-number">
                    <span className="step-number-text">01</span>
                  </div>
                  <span className="step-badge pa-badge-uploaded">{t('step_badge_uploaded')}</span>
                </div>
                <div className="step-card-body">
                  <p className="step-card-title step-card-title-active">{t('photo_uploaded_title')}</p>
                </div>
                <div className="pa-thumbnails">
                  <PhotoThumb previewUrl={frontPreviewUrl} label={t('thumb_label_front')} />
                  <PhotoThumb previewUrl={sidePreviewUrl} label={t('thumb_label_side')} />
                </div>
                <button className="pa-edit-link" onClick={() => setShowModal(true)}>
                  {t('edit_btn')}
                </button>
              </div>
            )}

            <div className="step-card step-card-active pa-id-card">
              <div className="step-card-header">
                <div className="step-number step-number-active" style={{ background: '#269c6e' }}>
                  <span className="step-number-text">{showPhotos ? '02' : '01'}</span>
                </div>
                <span className="step-badge step-badge-active">{t('step_badge_active')}</span>
              </div>
              <div className="step-card-body">
                <p className="step-card-title step-card-title-active">{t('id_title')}</p>
                <p className="step-card-desc step-card-desc-active">
                  {t('id_desc')}
                </p>
              </div>
              {!jumioSrc && (
                <div className="step-card-action" style={{ marginTop: 'auto' }}>
                  <button
                    className="pending-action-btn pending-action-btn-primary"
                    onClick={!jumioLoading ? handleStartJumio : undefined}
                    disabled={jumioLoading}
                  >
                    {jumioLoading ? t('id_btn_loading') : t('id_btn')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {jumioSrc && (
            <div className="pending-actions-jumio-wrapper">
              <iframe src={jumioSrc} allow="camera; microphone" title="ID Verification" />
            </div>
          )}

          {errorMessage && <p className="pending-actions-error">{errorMessage}</p>}

          <div className="pending-actions-divider" />
          <p className="pending-actions-footer">
            {t('footer')}
          </p>
        </div>

        {showModal && (
          <PhotoUploadModal
            hashID={hashID}
            countryID={countryID}
            language={language}
            isWaitingActions={isWaitingActions}
            initialFrontUrl={frontPreviewUrl}
            initialSideUrl={sidePreviewUrl}
            onClose={() => setShowModal(false)}
            onBothUploaded={handleBothUploaded}
          />
        )}
      </>
    );
  }

  /* ── Photos on file, no open verification task: always allow view/edit ── */
  if (frontPreviewUrl || sidePreviewUrl || photoUploadDone) {
    return (
      <div className="pending-actions-card">
        <div className="step-card step-card-uploaded pa-photo-only-card">
          <div className="pa-photo-only-header">
            <div className="pa-done-circle pa-done-circle-check">
              <img src={checkIcon} alt="" className="pa-done-check" />
            </div>
            <div className="step-number step-number-active pa-done-circle-number">
              <span className="step-number-text">01</span>
            </div>
            <div className="pa-photo-only-text">
              <p className="step-card-title step-card-title-active">{t('photo_uploaded_title')}</p>
              <p className="pa-photo-only-subtitle">{t('subtitle_thank_you')}</p>
            </div>
            <span className="step-badge pa-badge-uploaded">{t('step_badge_uploaded')}</span>
          </div>

          <div className="step-card-body pa-photo-only-mobile-body">
            <p className="step-card-title step-card-title-active">{t('photo_uploaded_title')}</p>
            <p className="step-card-desc step-card-desc-active">{t('subtitle_thank_you')}</p>
          </div>

          <div className="pa-wide-thumbs">
            <WidePhotoThumb previewUrl={frontPreviewUrl} label={t('thumb_label_front')} />
            <WidePhotoThumb previewUrl={sidePreviewUrl} label={t('thumb_label_side')} />
          </div>

          <button className="pa-edit-link pa-photo-only-edit" onClick={() => setShowModal(true)}>
            {t('edit_btn')}
          </button>
        </div>

        <div className="pending-actions-divider" />
        <p className="pending-actions-footer">
          {t('footer')}
        </p>

        {showModal && (
          <PhotoUploadModal
            hashID={hashID}
            countryID={countryID}
            language={language}
            isWaitingActions={isWaitingActions}
            initialFrontUrl={frontPreviewUrl}
            initialSideUrl={sidePreviewUrl}
            onClose={() => setShowModal(false)}
            onBothUploaded={handleBothUploaded}
          />
        )}
      </div>
    );
  }

  /* ── State 2d: ID-only order, verification started but not completed (page reloaded / Jumio session expired) ── */
  // Without this branch the card rendered nothing, leaving the customer no way to retry (HL-11633).
  if (requiresIdVerification && idVerificationInProgress) {
    return (
      <div className="pending-actions-card">
        <div className="pending-actions-header">
          <p className="pending-actions-title">{jumioSubmitted ? t('title_thank_you') : t('title_one')}</p>
          <p className="pending-actions-subtitle">
            {jumioSubmitted ? t('subtitle_thank_you') : t('subtitle')}
          </p>
        </div>

        <div className="pending-actions-steps pending-actions-steps-single">
          <div className="step-card step-card-uploaded pa-id-card">
            <div className="step-card-header">
              <div className="pa-done-circle pa-done-circle-check">
                <img src={checkIcon} alt="" className="pa-done-check" />
              </div>
              <div className="step-number step-number-active pa-done-circle-number">
                <span className="step-number-text">02</span>
              </div>
              <span className="step-badge pa-badge-uploaded">{t('step_badge_uploaded')}</span>
            </div>
            <div className="step-card-body">
              <p className="step-card-title step-card-title-active">{t('id_uploaded_title')}</p>
              <p className="step-card-desc step-card-desc-active">
                {jumioSubmitted ? t('id_uploaded_desc') : t('id_pending_desc')}
              </p>
            </div>
            {!jumioSubmitted && !jumioSrc && (
              <div className="step-card-action" style={{ marginTop: 'auto' }}>
                <button
                  className="pending-action-btn pending-action-btn-primary"
                  onClick={!jumioLoading ? handleStartJumio : undefined}
                  disabled={jumioLoading}
                >
                  {jumioLoading ? t('id_btn_loading') : t('id_retry_btn')}
                </button>
              </div>
            )}
          </div>
        </div>

        {jumioSrc && !jumioSubmitted && (
          <div className="pending-actions-jumio-wrapper">
            <iframe src={jumioSrc} allow="camera; microphone" title="ID Verification" />
          </div>
        )}

        {errorMessage && <p className="pending-actions-error">{errorMessage}</p>}

        <div className="pending-actions-divider" />
        <p className="pending-actions-footer">
          {t('footer')}
</p>
      </div>
    );
  }

  /* ── State 2d: ID-only order, verification started but not completed (page reloaded / Jumio session expired) ── */
  // Without this branch the card rendered nothing, leaving the customer no way to retry (HL-11633).
  if (requiresIdVerification && idVerificationInProgress) {
    return (
      <div className="pending-actions-card">
        <div className="pending-actions-header">
          <p className="pending-actions-title">{jumioSubmitted ? t('title_thank_you') : t('title_one')}</p>
          <p className="pending-actions-subtitle">
            {jumioSubmitted ? t('subtitle_thank_you') : t('subtitle')}
          </p>
        </div>

        <div className="pending-actions-steps pending-actions-steps-single">
          <div className="step-card step-card-uploaded pa-id-card">
            <div className="step-card-header">
              <div className="pa-done-circle pa-done-circle-check">
                <img src={checkIcon} alt="" className="pa-done-check" />
              </div>
              <div className="step-number step-number-active pa-done-circle-number">
                <span className="step-number-text">02</span>
              </div>
              <span className="step-badge pa-badge-uploaded">{t('step_badge_uploaded')}</span>
            </div>
            <div className="step-card-body">
              <p className="step-card-title step-card-title-active">{t('id_uploaded_title')}</p>
              <p className="step-card-desc step-card-desc-active">
                {jumioSubmitted ? t('id_uploaded_desc') : t('id_pending_desc')}
              </p>
            </div>
            {!jumioSubmitted && !jumioSrc && (
              <div className="step-card-action" style={{ marginTop: 'auto' }}>
                <button
                  className="pending-action-btn pending-action-btn-primary"
                  onClick={!jumioLoading ? handleStartJumio : undefined}
                  disabled={jumioLoading}
                >
                  {jumioLoading ? t('id_btn_loading') : t('id_retry_btn')}
                </button>
              </div>
            )}
          </div>
        </div>

        {jumioSrc && !jumioSubmitted && (
          <div className="pending-actions-jumio-wrapper">
            <iframe src={jumioSrc} allow="camera; microphone" title="ID Verification" />
          </div>
        )}

        {errorMessage && <p className="pending-actions-error">{errorMessage}</p>}

        <div className="pending-actions-divider" />
        <p className="pending-actions-footer">
          {t('footer')}
</p>
      </div>
    );
  }

  /* ── State 2d: ID-only order, verification started but not completed (page reloaded / Jumio session expired) ── */
  // Without this branch the card rendered nothing, leaving the customer no way to retry (HL-11633).
  if (requiresIdVerification && idVerificationInProgress) {
    return (
      <div className="pending-actions-card">
        <div className="pending-actions-header">
          <p className="pending-actions-title">{jumioSubmitted ? t('title_thank_you') : t('title_one')}</p>
          <p className="pending-actions-subtitle">
            {jumioSubmitted ? t('subtitle_thank_you') : t('subtitle')}
          </p>
        </div>

        <div className="pending-actions-steps pending-actions-steps-single">
          <div className="step-card step-card-uploaded pa-id-card">
            <div className="step-card-header">
              <div className="pa-done-circle pa-done-circle-check">
                <img src={checkIcon} alt="" className="pa-done-check" />
              </div>
              <div className="step-number step-number-active pa-done-circle-number">
                <span className="step-number-text">02</span>
              </div>
              <span className="step-badge pa-badge-uploaded">{t('step_badge_uploaded')}</span>
            </div>
            <div className="step-card-body">
              <p className="step-card-title step-card-title-active">{t('id_uploaded_title')}</p>
              <p className="step-card-desc step-card-desc-active">
                {jumioSubmitted ? t('id_uploaded_desc') : t('id_pending_desc')}
              </p>
            </div>
            {!jumioSubmitted && !jumioSrc && (
              <div className="step-card-action" style={{ marginTop: 'auto' }}>
                <button
                  className="pending-action-btn pending-action-btn-primary"
                  onClick={!jumioLoading ? handleStartJumio : undefined}
                  disabled={jumioLoading}
                >
                  {jumioLoading ? t('id_btn_loading') : t('id_retry_btn')}
                </button>
              </div>
            )}
          </div>
        </div>

        {jumioSrc && !jumioSubmitted && (
          <div className="pending-actions-jumio-wrapper">
            <iframe src={jumioSrc} allow="camera; microphone" title="ID Verification" />
          </div>
        )}

        {errorMessage && <p className="pending-actions-error">{errorMessage}</p>}

        <div className="pending-actions-divider" />
        <p className="pending-actions-footer">
          {t('footer')}
</p>
      </div>
    );
  }

  return null;
};

interface PhotoThumbProps {
  previewUrl: string | null;
  label: string;
}

const WidePhotoThumb: React.FC<PhotoThumbProps> = ({ previewUrl, label }) => {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="pa-wide-thumb">
      <div className="pa-wide-thumb-preview">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={label}
            className="pa-wide-thumb-img"
            style={revealed ? { filter: 'none', transform: 'none' } : undefined}
          />
        ) : (
          <div className="pa-wide-thumb-placeholder" />
        )}
      </div>
      <div className="pa-thumb-label" style={{ cursor: 'pointer' }} onClick={() => setRevealed(r => !r)}>
        <div className="pa-thumb-eye-wrap">
          <img src={eyeIcon} alt="" className="pa-thumb-eye-icon" />
        </div>
        <span className="pa-thumb-label-text">{label}</span>
      </div>
    </div>
  );
};

const PhotoThumb: React.FC<PhotoThumbProps> = ({ previewUrl, label }) => {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="pa-thumb">
      <div className="pa-thumb-preview">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={label}
            className="pa-thumb-img"
            style={revealed ? { filter: 'none', transform: 'none' } : undefined}
          />
        ) : (
          <div className="pa-thumb-placeholder" />
        )}
      </div>
      <div className="pa-thumb-label" style={{ cursor: 'pointer' }} onClick={() => setRevealed(r => !r)}>
        <div className="pa-thumb-eye-wrap">
          <img src={eyeIcon} alt="" className="pa-thumb-eye-icon" />
        </div>
        <span className="pa-thumb-label-text">{label}</span>
      </div>
    </div>
  );
};

export default MyAccountPendingActions;
